import React, { useState, useEffect, useMemo } from "react";
import { serverFetch, useGetIsDesktop } from "@extensions";
import { useUser } from "@redux";
import { message, Spin, Card, Button, Space, Table } from "antd";
import { ReloadOutlined, PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./sales.css";
export let refreshActiveSales = () => {};
export let refreshActiveLoading = false;

const SalesActive = () => {
  const user = useUser();
  const isDesktop = useGetIsDesktop();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSales();
    }
  }, [user]);

  const fetchSales = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const queryParams = {
        showPaid: true, // Оплаченные
      };

      const data = await serverFetch("OnlineClient/Sales", { method: "GET", queryParams: queryParams }, user);
      // Фильтруем только активные (оплаченные, но не использованные)
      // Предполагаем, что использованные имеют флаг used или visitFinish
      const activeSales = (data || []).filter(sale => !sale.used && !sale.visitFinish);
      setSales(activeSales);
    } catch (error) {
      console.error("Ошибка получения продаж:", error);
      message.error(error?.UserMessage || "Не удалось загрузить список покупок");
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  refreshActiveSales = fetchSales;
  refreshActiveLoading = loading;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dayjs(dateStr).format("DD.MM.YYYY");
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return dayjs(timeStr, "HH:mm:ss").format("HH:mm");
  };

  const formatPrice = (price) => {
    if (!price) return "0";
    return price.toLocaleString("ru-RU");
  };

  const getTicketUrls = (sale) => {
    if (!Array.isArray(sale?.ticketUrls)) return [];
    return sale.ticketUrls.filter((url) => typeof url === "string" && url.trim());
  };

  const openTicketUrl = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderTicketIcons = (sale) => {
    const ticketUrls = getTicketUrls(sale);
    if (!ticketUrls.length) return null;

    return (
      <span className="sales-ticket-icons">
        {ticketUrls.map((url, idx) => (
          <PaperClipOutlined
            key={`${sale.id}-ticket-${idx}`}
            className="sales-ticket-icon"
            onClick={() => openTicketUrl(url)}
            title={`Открыть чек ${idx + 1}`}
          />
        ))}
      </span>
    );
  };

  const formatRecord = (sale) => {
    if (!sale.recordDate) return "";
    const date = formatDate(sale.recordDate);
    const start = sale.recordStart ? formatTime(sale.recordStart) : "";
    const finish = sale.recordFinish ? formatTime(sale.recordFinish) : "";
    if (start && finish) {
      return `${date} ${start}-${finish}`;
    }
    return date;
  };

  const columns = useMemo(() => [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Дата",
      dataIndex: "docDate",
      key: "docDate",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Наименование",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Кол.",
      dataIndex: "qty",
      key: "qty",
      width: 80,
      align: "right",
    },
    {
      title: "Сумма",
      dataIndex: "syma",
      key: "syma",
      width: 120,
      align: "right",
      render: (price, record) => (
        <span className="sales-price-with-tickets">
          {renderTicketIcons(record)}
          <span>{formatPrice(price)} руб.</span>
        </span>
      ),
    },
    {
      title: "Сотрудник",
      dataIndex: "recordUserName",
      key: "recordUserName",
      width: 150,
    },
    {
      title: "Запись",
      key: "record",
      width: 200,
      render: (_, record) => formatRecord(record),
    },
  ], []);

  return (
    <div className="sales-content">
      {loading ? (
        <div className="sales-loading"><Spin size="large" /></div>
      ) : isDesktop ? (
        sales.length > 0 ? (
          <Table columns={columns} dataSource={sales} rowKey="id" pagination={false} className="sales-table"/>
        ) : (
          <div className="sales-empty">Нет активных покупок</div>
        )
      ) : (
        <div className="sales-list">
          {sales.length > 0 ? (
            sales.map((sale) => (
              <Card key={sale.id} className="sales-card" size="small">
                <div className="sales-card-content">
                  <div className="sales-card-header">
                    <div className="sales-card-date">{formatDate(sale.docDate)}</div>
                    <div className="sales-card-price">
                      {renderTicketIcons(sale)}
                      <span>{formatPrice(sale.syma)} руб.</span>
                    </div>
                  </div>
                  {sale.deleted && <span className="sales-card-deleted">Удалено</span>}
                  {sale.status === 1 && <span className="sales-card-paid">Оплачено</span>}
                  <div className="sales-card-body">
                    <div className="sales-card-info">
                      {sale.productName && <div className="sales-card-product">{sale.productName}</div>}
                      {sale.qty && (
                        <div className="sales-card-qty">Количество: {sale.qty}</div>
                      )}
                      {sale.recordUserName && (
                        <div className="sales-card-employee">Сотрудник: {sale.recordUserName}</div>
                      )}
                      {sale.recordDate && (
                        <div className="sales-card-record">Запись: {formatRecord(sale)}</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="sales-empty">Нет активных покупок</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesActive;

